'use client';

import { motion } from 'framer-motion';
import { Check, Tag, Folder, Calendar } from 'lucide-react';

import { usePageBuilderContext } from '../page-builder-context';
import { CategorySelector } from '@/components/products/category-selector';
import { PreviewImagesUploader } from './preview-images-uploader';
import { Input } from '@/components/ui/input';
import { STATUS_TAG_OPTIONS } from '@/lib/validations/product-submission';
import type { StatusTag } from '@/lib/validations/product-submission';
import { cn } from '@/lib/utils';

// ============================================================================
// STATUS TAG SELECTOR
// ============================================================================

function StatusTagSelector() {
  const { formData, updateMetadata, errors } = usePageBuilderContext();
  const selectedTags = formData.metadata.statusTags;
  const hasError = errors.has('metadata.statusTags');

  const toggleTag = (tagValue: StatusTag) => {
    const isSelected = selectedTags.includes(tagValue);
    const newTags = isSelected
      ? selectedTags.filter((t) => t !== tagValue)
      : [...selectedTags, tagValue];
    updateMetadata('statusTags', newTags as StatusTag[]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-foreground-tertiary" />
        <span className="text-sm font-medium text-foreground">
          Что вы ищете?
        </span>
        <span className="text-xs text-destructive">*</span>
      </div>

      <div className="space-y-2">
        {STATUS_TAG_OPTIONS.map((option) => {
          const isSelected = selectedTags.includes(option.value as StatusTag);

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggleTag(option.value as StatusTag)}
              className={cn(
                'w-full flex items-center gap-3 p-3 text-left rounded-lg border',
                'transition-all duration-150',
                isSelected
                  ? 'border-primary bg-primary-subtle'
                  : 'border-border hover:border-border-hover hover:bg-muted/30'
              )}
            >
              <span className="text-lg">{option.label.split(' ')[0]}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">
                  {option.label.slice(3)}
                </div>
                <div className="text-xs text-foreground-secondary truncate">
                  {option.description}
                </div>
              </div>
              {isSelected && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  <Check className="h-5 w-5 text-primary-text flex-shrink-0" />
                </motion.div>
              )}
            </button>
          );
        })}
      </div>

      {hasError && (
        <p className="text-xs text-destructive">
          {errors.get('metadata.statusTags')?.[0]}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function MetadataSection() {
  const { formData, updateMetadata, categories, categoriesLoading, errors } = usePageBuilderContext();
  const hasError = errors.has('metadata.categoryIds');

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-8">
      <div>
        <h2 className="text-lg font-bold text-foreground mb-1">
          Последние штрихи
        </h2>
        <p className="text-sm text-foreground-secondary">
          Помогите людям найти ваш продукт
        </p>
      </div>

      {/* Categories */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Folder className="h-4 w-4 text-foreground-tertiary" />
          <span className="text-sm font-medium text-foreground">
            Категории (1-3)
          </span>
          <span className="text-xs text-destructive">*</span>
        </div>

        <CategorySelector
          categories={categories}
          value={formData.metadata.categoryIds}
          onChange={(ids) => updateMetadata('categoryIds', ids)}
          isLoading={categoriesLoading}
          error={hasError ? errors.get('metadata.categoryIds')?.[0] : undefined}
          maxCategories={3}
        />
      </div>

      {/* Status Tags */}
      <StatusTagSelector />

      {/* Launch Date */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-foreground-tertiary" />
          <span className="text-sm font-medium text-foreground">
            Дата запуска
          </span>
          <span className="text-xs text-foreground-tertiary">(необязательно)</span>
        </div>
        <Input
          type="date"
          value={formData.metadata.launchDate || ''}
          onChange={(e) => updateMetadata('launchDate', e.target.value || undefined)}
          className="max-w-xs"
        />
        <p className="text-xs text-foreground-tertiary">
          Укажите дату запуска продукта, если она известна
        </p>
      </div>

      {/* Preview Images */}
      <PreviewImagesUploader />
    </div>
  );
}
